// The nightly check-in: three sliders on scales that never change, a nap, some
// movement, one line, done in under a minute. Asked once a night and never
// chased. Saving lands on the evening's Day record; opening it again shows
// what was saved, and changes it.
import { useState, type CSSProperties } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useDays } from '@/db/hooks'
import { useNow } from '@/hooks/useNow'
import { clock } from '@/lib/day'
import { eveningOf, saveCheckin, type CheckinValues } from '@/lib/wellbeing'
import { Button, Field, PageTitle, Segmented } from '@/components/ui'

const Slider = ({
  label,
  low,
  high,
  value,
  onChange,
  testid,
}: {
  label: string
  low: string
  high: string
  value: number
  onChange: (v: number) => void
  testid: string
}) => (
  <label className="block mb-5">
    <span className="flex items-baseline justify-between mb-1">
      <span className="text-sm">{label}</span>
      <span className="text-sm tabular-nums text-muted" aria-hidden>
        {value}
      </span>
    </span>
    <input
      type="range"
      min={0}
      max={10}
      step={1}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="range-scale w-full"
      style={{ '--fill': `${value * 10}%` } as CSSProperties}
      aria-label={`${label}, 0 ${low} to 10 ${high}`}
      data-testid={testid}
    />
    <span className="flex justify-between text-[11px] text-faint mt-0.5">
      <span>{low}</span>
      <span>{high}</span>
    </span>
  </label>
)

export const CheckinView = () => {
  const navigate = useNavigate()
  const days = useDays()
  const now = useNow(60_000)
  const date = eveningOf(now)
  const rec = days.find((d) => d.spec.date === date)?.spec
  const [v, setV] = useState<CheckinValues>({
    anxiety: rec?.anxiety ?? 5,
    unreality: rec?.unreality ?? 5,
    mood: rec?.mood ?? 5,
    nap_min: rec?.nap_min ?? 0,
    exercise_min: rec?.exercise_min ?? 0,
    note: rec?.note ?? '',
  })
  const set = <K extends keyof CheckinValues>(k: K, val: CheckinValues[K]) =>
    setV((cur) => ({ ...cur, [k]: val }))
  return (
    <div className="flex flex-col gap-2" data-testid="checkin">
      <PageTitle>Check-in</PageTitle>
      <p className="text-sm text-muted -mt-2 mb-3">
        {rec?.checked_in_at
          ? `Saved at ${clock(rec.checked_in_at)}. Change anything and save again.`
          : 'Where today landed. A minute, then bed.'}
      </p>
      <Slider
        label="Anxiety"
        low="calm"
        high="the worst it gets"
        value={v.anxiety}
        onChange={(n) => set('anxiety', n)}
        testid="checkin-anxiety"
      />
      <Slider
        label="Unreality"
        low="here"
        high="not here at all"
        value={v.unreality}
        onChange={(n) => set('unreality', n)}
        testid="checkin-unreality"
      />
      <Slider
        label="Mood"
        low="low"
        high="high"
        value={v.mood}
        onChange={(n) => set('mood', n)}
        testid="checkin-mood"
      />
      <Field label="Nap">
        <Segmented
          value={v.nap_min}
          options={[
            { value: 0, label: 'None' },
            { value: 20, label: '20 min' },
            { value: 45, label: '45 min' },
            { value: 90, label: '90+' },
          ]}
          onChange={(n) => set('nap_min', n)}
          label="Nap"
        />
      </Field>
      <Field label="Moved">
        <Segmented
          value={v.exercise_min}
          options={[
            { value: 0, label: 'No' },
            { value: 15, label: '15 min' },
            { value: 30, label: '30 min' },
            { value: 60, label: '1 h+' },
          ]}
          onChange={(n) => set('exercise_min', n)}
          label="Exercise"
        />
      </Field>
      <Field label="One line" hint="Optional.">
        <input
          value={v.note}
          onChange={(e) => set('note', e.target.value)}
          maxLength={500}
          placeholder="Anything worth a line"
          className="w-full"
          data-testid="checkin-note"
        />
      </Field>
      <div className="flex gap-2 mt-2">
        <Button
          big
          variant="primary"
          onClick={() => void saveCheckin(date, v).then(() => navigate({ to: '/wellbeing' }))}
          data-testid="checkin-save"
        >
          Save
        </Button>
      </div>
    </div>
  )
}
